import { useState, useEffect, useCallback, useRef } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { DataTable } from '../components/shared/DataTable';
import { ViewHistoryModal } from '../components/phone-numbers/ViewHistoryModal';
import { SyncResultModal } from '../components/phone-numbers/SyncResultModal';
import { searchCallPhoneNumbers, updateCallPhoneNumber, syncTwilioActiveNumbers, syncRetellActiveNumbers, importPhoneToRetell, deletePhoneFromRetell } from '../services/callPhoneNumberService';
import type { SyncTwilioResult } from '../services/callPhoneNumberService';
import { useProfileStore } from '../store/useProfileStore';
import { isCompanyShopSuperAdmin } from '../types/profile';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore';
import { useRefresh } from '../contexts/RefreshContext';
import type { CallPhoneNumber, CallPhoneNumberStatus } from '../types/callPhoneNumber';
import type { Column } from '../components/shared/DataTable';

const STATUS_PRIORITY: Record<string, number> = {
  new: 0, changed: 1, active: 2, inactive: 3, burned: 4, deleted: 5,
};

const columns: Column<CallPhoneNumber>[] = [
  { key: 'provider', label: 'Provider', render: (item) => item.provider.replace(/\b\w/g, (c) => c.toUpperCase()) },
  { key: 'status', label: 'Status' },
  { key: 'phoneNumber', label: 'Phone Number' },
];

function RowMenu({ item, onStatusChange, onImportRetell, onDeleteRetell }: {
  item: CallPhoneNumber;
  onStatusChange: (item: CallPhoneNumber, status: CallPhoneNumberStatus) => void;
  onImportRetell: (item: CallPhoneNumber) => void;
  onDeleteRetell: (item: CallPhoneNumber) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const isTwilio = item.provider === 'twilio';
  const canSetActive = !isTwilio && (item.status === 'new' || item.status === 'changed' || item.status === 'burned' || item.status === 'inactive');
  const canSetInactive = isTwilio
    ? item.status === 'new'
    : item.status === 'new' || item.status === 'changed' || item.status === 'active' || item.status === 'burned';
  const canBurn = !isTwilio && item.status === 'active';
  const canSetNew = isTwilio && item.status === 'inactive';
  const canImportRetell = isTwilio && item.status === 'new';
  const canDeleteRetell = !isTwilio && (item.status === 'inactive' || item.status === 'burned');

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
          {!isTwilio && (
            <button
              disabled={!canSetActive}
              onClick={() => { setOpen(false); onStatusChange(item, 'active'); }}
              className={`w-full text-left px-3 py-1.5 text-sm ${canSetActive ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Set Active
            </button>
          )}
          {!isTwilio && (
            <button
              disabled={!canBurn}
              onClick={() => { setOpen(false); onStatusChange(item, 'burned'); }}
              className={`w-full text-left px-3 py-1.5 text-sm ${canBurn ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Burn
            </button>
          )}
          <button
            disabled={!canSetInactive}
            onClick={() => { setOpen(false); onStatusChange(item, 'inactive'); }}
            className={`w-full text-left px-3 py-1.5 text-sm ${canSetInactive ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
          >
            Set Inactive
          </button>
          {isTwilio && (
            <button
              disabled={!canSetNew}
              onClick={() => { setOpen(false); onStatusChange(item, 'new'); }}
              className={`w-full text-left px-3 py-1.5 text-sm ${canSetNew ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Set as New
            </button>
          )}
          {isTwilio && (
            <button
              disabled={!canImportRetell}
              onClick={() => { setOpen(false); onImportRetell(item); }}
              className={`w-full text-left px-3 py-1.5 text-sm ${canImportRetell ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Import to Retell
            </button>
          )}
          {!isTwilio && (
            <button
              disabled={!canDeleteRetell}
              onClick={() => { setOpen(false); onDeleteRetell(item); }}
              className={`w-full text-left px-3 py-1.5 text-sm ${canDeleteRetell ? 'text-red-600 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Delete from Retell
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PhoneNumbersPage() {
  const location = useLocation();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const isSuperAdmin = isCompanyShopSuperAdmin(activeProfile);

  const [data, setData] = useState<CallPhoneNumber[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [viewHistoryId, setViewHistoryId] = useState<string | null>(null);
  const [manageMenuOpen, setManageMenuOpen] = useState(false);
  const [showTwilio, setShowTwilio] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncTwilioResult | null>(null);
  const manageRef = useRef<HTMLDivElement>(null);
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);

  useEffect(() => {
    if (location.pathname !== '/phone-numbers') return;
    setBreadcrumb([{ label: 'From Numbers' }]);
  }, [location.pathname, setBreadcrumb]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchCallPhoneNumbers({}, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  const filteredData = showTwilio ? data.filter((d) => d.provider === 'twilio') : data.filter((d) => d.provider !== 'twilio');

  const sortedData = [...filteredData].sort((a, b) => {
    const at = a.provider === 'twilio' ? 1 : 0;
    const bt = b.provider === 'twilio' ? 1 : 0;
    if (at !== bt) return at - bt;
    const pc = (a.provider || '').localeCompare(b.provider || '');
    if (pc !== 0) return pc;
    const sa = STATUS_PRIORITY[a.status] ?? 99;
    const sb = STATUS_PRIORITY[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return (a.phoneNumber || '').localeCompare(b.phoneNumber || '');
  });

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/phone-numbers', fetch);
    return () => unregisterRefreshHandler('/phone-numbers');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  useEffect(() => {
    if (!manageMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (manageRef.current && !manageRef.current.contains(e.target as Node)) setManageMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [manageMenuOpen]);

  const handleStatusChange = async (item: CallPhoneNumber, status: CallPhoneNumberStatus) => {
    await updateCallPhoneNumber(item.id, { ...item, status });
    fetch();
  };

  const handleImportRetell = async (item: CallPhoneNumber) => {
    await importPhoneToRetell(item.phoneNumber);
    setShowTwilio(false);
    fetch();
  };

  const handleDeleteRetell = async (item: CallPhoneNumber) => {
    await deletePhoneFromRetell(item.phoneNumber);
    fetch();
  };

  const handleSyncTwilio = async () => {
    setManageMenuOpen(false);
    setSyncing(true);
    try {
      const result = await syncTwilioActiveNumbers();
      setSyncResult(result);
      fetch();
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncRetell = async () => {
    setManageMenuOpen(false);
    setSyncing(true);
    try {
      const result = await syncRetellActiveNumbers();
      setSyncResult(result);
      fetch();
    } finally {
      setSyncing(false);
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div className="p-4 sm:p-6 lg:p-8">
          <DataTable
            title="From Numbers"
            columns={columns}
            data={sortedData}
            page={page}
            pageSize={pageSize}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onRefresh={fetch}
            rowClassName={(item) => item.provider === 'twilio' ? 'bg-gray-50' : ''}
            headerControls={isSuperAdmin ? (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showTwilio}
                    onClick={() => { setShowTwilio(!showTwilio); setPage(1); }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showTwilio ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${showTwilio ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                  </button>
                  Twilio
                </label>
              <div ref={manageRef} className="relative">
                <button
                  onClick={() => setManageMenuOpen(!manageMenuOpen)}
                  disabled={syncing}
                  className="px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Manage Providers'}
                </button>
                {manageMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[220px]">
                    <button
                      onClick={handleSyncRetell}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Sync Retell Active Numbers
                    </button>
                    <button
                      onClick={handleSyncTwilio}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Sync Twilio Active Numbers
                    </button>
                  </div>
                )}
              </div>
              </div>
            ) : undefined}
            actions={(item) => item.provider === 'twilio' ? null : (
              <button
                onClick={() => setViewHistoryId(item.id)}
                className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
              >
                View History
              </button>
            )}
            rowMenu={isSuperAdmin ? (item) => (
              <RowMenu item={item} onStatusChange={handleStatusChange} onImportRetell={handleImportRetell} onDeleteRetell={handleDeleteRetell} />
            ) : undefined}
          />

          {viewHistoryId && (
            <ViewHistoryModal
              callPhoneNumberId={viewHistoryId}
              onClose={() => setViewHistoryId(null)}
            />
          )}

          {syncResult && (
            <SyncResultModal
              result={syncResult}
              onClose={() => setSyncResult(null)}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
